import $ from 'jquery';
import ObjectUtil from '@core/util/object';
import Feature from '../util/FeatureUtil';
import ResponsiveBagProductDecider from '../util/ResponsiveBagProductDecider';
import ProsController from './ProsController';
import Constants from '../util/Constants';
import ProsDOMUtil from '../util/ProsDOMUtil';
import Util from '../util/Util';
import KillSwitchUtil from '../util/KillSwitchUtil';

/**
 * Experiment Logic: Behind isProsZonesExpansionEnabled killswitch + Behind client side mercury experiment recipe Id
 *
 * 1. Read last and second-last item from data, data is bagObject here
 * 2. last item: set it in data-productid for data-context=SB_ZONE_A pros-panel
 * 3. second-last item: set it in data-productid for data-context=SB_ZONE_B
 *
 * Control logic: copy paste from responsive bag ProsInitialier.js
 *
 * bagObject sample:
 */

const SB_ZONE_A_SELECTOR = 'div[data-context="SB_ZONE_A"]';
const SB_ZONE_B_SELECTOR = 'div[data-context="SB_ZONE_B"]';
const SB_ZONE_EMPTY = 'div[data-context="SB_ZONE_EMPTY"]';

export default class ResponsiveBagController {
  constructor(data, options) {
    this.data = data;
    this.options = options;
    this.responsiveBagProductDecider = new ResponsiveBagProductDecider(this.data);
    if (!KillSwitchUtil.isTrue('isProsOnEmptyBagEnabled') && this.responsiveBagProductDecider.emptyCart()) {
      ResponsiveBagController.applyChangesForEmptyCart(SB_ZONE_A_SELECTOR);
    }

    this.zoneA = $(SB_ZONE_A_SELECTOR);
    this.zoneB = $(SB_ZONE_B_SELECTOR);
    this.zoneEmpty = $(SB_ZONE_EMPTY);
  }

  prosConfiguration() {
    return { page: this.options.page, selector: Constants.CLASS_SELECTOR, freeShippingThresholdReminderAmount: ObjectUtil.getChildProperty(this.data, 'sections.shippingThreshold.data.freeShippingThresholdReminderAmountInDouble') };
  }

  render() {
    if (KillSwitchUtil.isTrue('isProsOnEmptyBagEnabled') && this.responsiveBagProductDecider.emptyCart()) {
      this.renderForEmptyBag();
    } else {
      this.renderForNonEmptyBag();
    }
  }

  renderForEmptyBag() {
    /* Get the PROS recommendation based on the most Recently Viewed item if present,
    otherwise generic product id -9 */
    const recentlyViewProducts = Util.getRecentlyViewedItems();
    const productId = (recentlyViewProducts && recentlyViewProducts.length > 0) ? recentlyViewProducts[0] : -9;
    const emptyBagZoneData = { productId };
    if (ResponsiveBagController.needToRender(emptyBagZoneData, this.zoneEmpty)) {
      // Hide Zone-A, if item removed from bag
      ResponsiveBagController.hideIt(this.zoneA);
      // Also reset Zone-A datacontext
      ResponsiveBagController.setDataContext(this.zoneA, {});
      ResponsiveBagController.setDataContext(this.zoneEmpty, emptyBagZoneData);
      this.zoneEmpty.addClass(Constants.CLASS_SELECTOR_CLASS).show();
      // delegate rendering part to ProsController.js
      ResponsiveBagController.renderPros(this.prosConfiguration());
    } else {
      ResponsiveBagController.avoidRerendering(this.zoneEmpty);
    }
  }

  renderForNonEmptyBag() {
    const refreshUnavailableZone = this.options && this.options.refreshUnavailableZone;
    Promise.all([Feature.BAG_PAGE_RVI_PROS_ZONE_ENABLED.isEnabled(),
      Feature.BAG_PAGE_MULTI_ZONE_C_EXPERIMENT.isEnabled()])
      .then(([rviZoneEnabled, multiZoneCEnabled]) => {
        if (multiZoneCEnabled && refreshUnavailableZone) {
          this.lookForRedesignMultiZoneC();
        } else {
          this.lookForZoneA();
          if (rviZoneEnabled) {
            this.lookForZoneB();
          }
          if (multiZoneCEnabled) {
            this.lookForRedesignMultiZoneC();
          }
        }
        // delegate rendering part to ProsController.js
        ResponsiveBagController.renderPros(this.prosConfiguration());
      });
  }

  lookForZoneA() {
    // Get and set the product-ids on div
    const zoneAData = this.responsiveBagProductDecider.getZoneAData();

    if (zoneAData) {
      // Hide Zone-Empty
      ResponsiveBagController.hideIt(this.zoneEmpty);
      ResponsiveBagController.setDataContext(this.zoneEmpty, {});
      if (ResponsiveBagController.needToRender(zoneAData, this.zoneA)) {
        ResponsiveBagController.setDataContext(this.zoneA, zoneAData);
        this.zoneA.addClass(Constants.CLASS_SELECTOR_CLASS).show();
      } else {
        ResponsiveBagController.avoidRerendering(this.zoneA);
      }
    } else {
      // on remove action, if empty cart, hide the existing rendered one
      ResponsiveBagController.hideIt(this.zoneA);
    }
  }

  lookForZoneB() {
    const zoneBData = this.responsiveBagProductDecider.getZoneBData();
    if (zoneBData) {
      if (ResponsiveBagController.needToRender(zoneBData, this.zoneB) || this.responsiveBagProductDecider.emptyCart()) {
        ResponsiveBagController.setDataContext(this.zoneB, zoneBData);
        this.zoneB.addClass(Constants.CLASS_SELECTOR_CLASS).show();
      } else {
        ResponsiveBagController.avoidRerendering(this.zoneB);
      }
    } else {
      // on remove action, if item for zone b, hide the existing rendered one
      ResponsiveBagController.hideIt(this.zoneB);
    }
  }

  lookForRedesignMultiZoneC() {
    const zoneCData = this.responsiveBagProductDecider.getBagZoneCData();

    if (zoneCData.length > 0) {
      this.renderZoneC(zoneCData, 'bag-product');
    }

    return zoneCData.length > 0;
  }

  renderZoneC(zoneCData, unavailableSource) {
    zoneCData.forEach((zoneItemData) => {
      if (zoneItemData.productId) {
        const zoneEl = document.createElement('div');
        let zoneC = $(zoneEl).attr('id', `${Constants.SB_ZONES.SB_ZONE_C}_${zoneItemData.productId}`);
        // add pros selector class
        zoneC.addClass(Constants.CLASS_SELECTOR_CLASS);
        // append zone to bag item container
        $(`#${unavailableSource}_${zoneItemData.productId}`).append(zoneC);
        // select zone C dom bound, cus data store only works properly if element is attach to the dom
        zoneC = $(`#${Constants.SB_ZONES.SB_ZONE_C}_${zoneItemData.productId}`);
        // add context type
        zoneC.data('context', `${Constants.SB_ZONES.SB_ZONE_C}`);
        zoneC.data('fccFallbackEnabled', false);
        zoneC.data('noDefaultRecs', true);
        zoneC.data('properties', {
          slimProductThumbnailEnabled: true,
          prosZoneRedesignEnabled: true,
          prosZonesAddToListEnabled: false,
        });
        ResponsiveBagController.updateHeaderAndTitle(zoneC, zoneItemData.name);
        // add zone data
        ResponsiveBagController.setDataContext(zoneC, zoneItemData);
      }
    });
  }

  static renderPros(configuration) {
    const controller = new ProsController(configuration);
    controller.render();
  }

  static setDataContext(element, data) {
    element
      .data('productid', data.productId)
      .data('categoryid', data.categoryId)
      .data('productIds', data.productIds);
  }

  static isZonePopulatedAndVisible(zoneEl) {
    if (zoneEl && zoneEl.children && zoneEl.children.length && typeof zoneEl.is === 'function') {
      return zoneEl.is(':visible');
    }
    return false;
  }

  static needToRender(zoneNewData, zoneEl) {
    if (!zoneNewData) {
      return false;
    }

    const { productid } = zoneEl.data();
    return !productid || +zoneNewData.productId !== +productid || !ResponsiveBagController.isZonePopulatedAndVisible(zoneEl);
  }

  static avoidRerendering(zoneEl) {
    zoneEl.removeClass(Constants.CLASS_SELECTOR_CLASS);
  }

  static hideIt(zoneEl) {
    zoneEl.removeClass(Constants.CLASS_SELECTOR_CLASS).hide();
  }

  static applyChangesForEmptyCart(selector) {
    $(selector).parent()
      .removeClass('large-8 large-12')
      .removeClass('medium-8 medium-12')
      .addClass('empty-bag');
    ProsDOMUtil.forceWindowResize();
  }

  static updateHeaderAndTitle(zoneEl, productName) {
    const adTextEl = zoneEl.find('.poolHeaderText');
    const adText = adTextEl.text().replace(/v_productName/, productName);
    adTextEl.attr('title', adText);
    if (adText.length > 60) {
      adTextEl.text(`${adText.substring(0, Constants.MAX_HEADER_LENGTH)}...`);
    } else {
      adTextEl.text(adText);
    }
  }
}
